import React, { useState, useEffect } from 'react';
import { Palette, Upload, X, Image, FileImage, AlertCircle } from 'lucide-react';
import { whiteLabelService } from '../../../services/whiteLabelService';

export default function WhiteLabelTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    brandName: '',
    slogan: '',
    logoPath: null,
    faviconPath: null,
    logoUrl: null,
    faviconUrl: null
  });
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await whiteLabelService?.getSettings();
      setSettings(data);
      if (data?.logoUrl) setLogoPreview(data?.logoUrl);
      if (data?.faviconUrl) setFaviconPreview(data?.faviconUrl);
    } catch (err) {
      setError(err?.message || 'Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file?.size > 5242880) {
      setError('Logo file size must be less than 5MB');
      return;
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!validTypes?.includes(file?.type)) {
      setError('Logo must be JPEG, PNG, SVG, or WebP');
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleFaviconChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file?.size > 5242880) {
      setError('Favicon file size must be less than 5MB');
      return;
    }

    // Validate file type
    const validTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/svg+xml'];
    if (!validTypes?.includes(file?.type)) {
      setError('Favicon must be ICO, PNG, or SVG');
      return;
    }

    setFaviconFile(file);
    setFaviconPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setSettings({ ...settings, logoPath: null, logoUrl: null });
  };

  const handleRemoveFavicon = () => {
    setFaviconFile(null);
    setFaviconPreview(null);
    setSettings({ ...settings, faviconPath: null, faviconUrl: null });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      let logoPath = settings?.logoPath;
      let faviconPath = settings?.faviconPath;

      // Upload new logo if selected
      if (logoFile) {
        // Delete old logo if exists
        if (settings?.logoPath) {
          await whiteLabelService?.deleteFile(settings?.logoPath);
        }
        logoPath = await whiteLabelService?.uploadLogo(logoFile);
      }

      // Upload new favicon if selected
      if (faviconFile) {
        // Delete old favicon if exists
        if (settings?.faviconPath) {
          await whiteLabelService?.deleteFile(settings?.faviconPath);
        }
        faviconPath = await whiteLabelService?.uploadFavicon(faviconFile);
      }

      // Update settings
      await whiteLabelService?.updateSettings({
        brandName: settings?.brandName,
        slogan: settings?.slogan,
        logoPath,
        faviconPath
      });

      setSuccess('Branding settings updated successfully!');
      setLogoFile(null);
      setFaviconFile(null);
      
      // Reload settings to get new URLs
      await loadSettings();

      // Reload page to apply new branding
      setTimeout(() => {
        window.location?.reload();
      }, 1500);
    } catch (err) {
      setError(err?.message || 'Failed to update branding settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Palette className="w-8 h-8 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">White Label Branding</h2>
        </div>
        <p className="text-gray-600">
          Customize your platform with your own branding. Upload a logo and favicon, or simply set a brand name and slogan.
        </p>
      </div>
      {/* Error/Success Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-700 hover:text-red-900">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-700 hover:text-green-900">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}
      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Brand Name & Slogan */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Brand Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand Name
              </label>
              <input
                type="text"
                value={settings?.brandName}
                onChange={(e) => setSettings({ ...settings, brandName: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your Brand Name"
              />
              <p className="mt-1 text-sm text-gray-500">
                This will appear in the header (hidden if logo is uploaded)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slogan
              </label>
              <input
                type="text"
                value={settings?.slogan}
                onChange={(e) => setSettings({ ...settings, slogan: e?.target?.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your Brand Slogan"
              />
              <p className="mt-1 text-sm text-gray-500">
                A short tagline for your brand (hidden if logo is uploaded)
              </p>
            </div>
          </div>
        </div>

        {/* Logo Upload */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Image className="w-5 h-5" />
            Logo
          </h3>
          <div className="space-y-4">
            {/* Size Guidelines */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">📏 Logo Size Guidelines</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Recommended:</strong> 200px height × auto width (maintains aspect ratio)</li>
                <li>• <strong>Maximum:</strong> 5MB file size</li>
                <li>• <strong>Formats:</strong> JPEG, PNG, SVG, or WebP</li>
                <li>• <strong>Best Practice:</strong> Use transparent background (PNG/SVG) for professional look</li>
                <li>• <strong>Note:</strong> Logo will replace brand name and slogan when uploaded</li>
              </ul>
            </div>

            {logoPreview ? (
              <div className="relative inline-block">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="h-24 w-auto rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={handleRemoveLogo}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <label className="cursor-pointer">
                  <span className="text-blue-600 hover:text-blue-700 font-medium">
                    Click to upload logo
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/svg+xml,image/webp"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-gray-500 mt-2">
                  JPEG, PNG, SVG, or WebP (Max 5MB)
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Favicon Upload */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileImage className="w-5 h-5" />
            Favicon
          </h3>
          <div className="space-y-4">
            {faviconPreview ? (
              <div className="relative inline-block">
                <img
                  src={faviconPreview}
                  alt="Favicon preview"
                  className="h-16 w-16 rounded-lg border border-gray-200"
                />
                <button
                  type="button"
                  onClick={handleRemoveFavicon}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <label className="cursor-pointer">
                  <span className="text-blue-600 hover:text-blue-700 font-medium">
                    Click to upload favicon
                  </span>
                  <input
                    type="file"
                    accept="image/x-icon,image/vnd.microsoft.icon,image/png,image/svg+xml"
                    onChange={handleFaviconChange}
                    className="hidden"
                  />
                </label>
                <p className="text-sm text-gray-500 mt-2">
                  ICO, PNG, or SVG (Max 5MB)
                </p>
              </div>
            )}
            <p className="text-sm text-gray-600">
              The favicon appears in browser tabs and bookmarks. Recommended size: 32x32 or 16x16 pixels.
            </p>
          </div>
        </div>

        {/* Preview - Updated to show conditional rendering */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              {logoPreview ? (
                // Logo-only preview
                <img src={logoPreview} alt="Logo" className="h-10 w-auto object-contain" />
              ) : (
                // Icon with brand name and slogan preview
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600"></div>
                  {(settings?.brandName || settings?.slogan) && (
                    <div>
                      {settings?.brandName && (
                        <h4 className="text-lg font-bold text-gray-900">
                          {settings?.brandName}
                        </h4>
                      )}
                      {settings?.slogan && (
                        <p className="text-sm text-gray-600">{settings?.slogan}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              {logoPreview 
                ? '✓ Logo will be displayed (brand name and slogan hidden)'
                : '✓ Brand name and slogan will be displayed (no logo)'}
            </p>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={loadSettings}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={saving}
          >
            Reset
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <span>Save Changes</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}